import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Save, FileSpreadsheet, Users, Table2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { fmtNum, MONTH_NAMES } from "@/lib/helpers";
import { HgoTable } from "@/components/targets/HgoTable";
import { canAct, isAdmin } from "@/lib/access";
import { TargetSettings } from "@/components/targets/TargetSettings";
import { PersonnelTargets } from "@/components/targets/PersonnelTargets";

function ScopeChips({ prog, scope, setScope }) {
  const chip = (id, label, r) => (
    <button key={id} type="button" onClick={() => setScope(id)} data-testid={`scope-chip-${id}`}
            className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors flex items-center gap-2 ${scope === id ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-900/60 border-slate-800 text-slate-300 hover:border-slate-600"}`}>
      {label}
      {r.total_target > 0 && <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${r.ok ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"}`}>%{r.hgo}</span>}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {chip("company", "Karahan Teknoloji (Toplam)", prog.company)}
      {prog.branches.map((b) => chip(b.scope_id, b.name, b))}
    </div>
  );
}

function BranchSummary({ branches, onPick }) {
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass" data-testid="branch-summary">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-900/80 text-[11px] font-mono uppercase tracking-wider text-slate-400">
            <tr><th className="px-4 py-2 text-left">Şube</th><th className="px-3 py-2 text-right">Hedef</th><th className="px-3 py-2 text-right">Gerçekleşen</th>
              <th className="px-3 py-2 text-center">HGO</th><th className="px-3 py-2 text-right">Tahmini</th><th className="px-3 py-2 text-center">Tahmini HGO</th><th className="px-3 py-2 text-center">%100</th></tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.scope_id} onClick={() => onPick(b.scope_id)} className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer" data-testid={`branch-summary-${b.scope_id}`}>
                <td className="px-4 py-2.5 font-semibold flex items-center gap-2"><Building2 size={14} className="text-slate-500" /> {b.name}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(b.total_target)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(b.total_sold)}</td>
                <td className={`px-3 py-2.5 text-center font-bold ${b.hgo >= 100 ? "text-emerald-300" : "text-red-300"}`}>{b.total_target ? `%${b.hgo}` : "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(b.forecast_qty)}</td>
                <td className={`px-3 py-2.5 text-center font-bold ${b.ok ? "text-emerald-300" : "text-red-300"}`}>{b.total_target ? `%${b.forecast_hgo}` : "—"}</td>
                <td className={`px-3 py-2.5 text-center font-bold ${b.ok ? "text-emerald-300" : "text-red-300"}`}>{b.total_target ? (b.ok ? "EVET" : "HAYIR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PersonnelBar({ branch, onToggle }) {
  const n = branch.staff_count;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border border-slate-800 bg-slate-900/60" data-testid="personnel-bar">
      <Users size={16} className="text-blue-300" />
      <span className="text-sm flex-1">
        Personel hedef dağıtımı: şube hedefi müdür hariç hedefe dahil <b>{n}</b> kişiye eşit bölünür (elle girilenler hariç)
        {branch.personnel_targets_enabled && n > 0 && branch.total_target > 0 && <span className="text-slate-400"> · kişi başı ≈ {fmtNum(Math.round((branch.total_target / n) * 10) / 10)} adet</span>}
      </span>
      <Switch checked={branch.personnel_targets_enabled} onCheckedChange={onToggle} data-testid="personnel-toggle" />
      <span className="text-xs text-slate-400 w-12">{branch.personnel_targets_enabled ? "Açık" : "Kapalı"}</span>
    </div>
  );
}

export default function Targets() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [prog, setProg] = useState(null);
  const [scope, setScope] = useState("company");
  const [draft, setDraft] = useState({});
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const canEdit = isAdmin(user);
  const [view, setView] = useState("");

  const load = async () => {
    const { data } = await http.get(`/targets/progress?year=${year}&month=${month}${view ? `&view=${view}` : ""}`);
    setProg(data);
  };
  const tick = useRealtimeTick();
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, tick, view]);
  useEffect(() => {
    if (!canEdit) return;
    Promise.all([http.get("/products"), http.get("/catalogs")]).then(([p, c]) => { setProducts(p.data); setCatalogs(c.data); });
  }, [canEdit]);

  const branch = prog?.branches?.find((b) => b.scope_id === scope);
  useEffect(() => {
    if (!branch) { setDraft({}); return; }
    const d = {};
    branch.items.forEach((it) => { d[it.item_id] = it.target_qty || ""; });
    setDraft(d);
  }, [scope, prog]); // eslint-disable-line

  const save = async () => {
    try {
      await http.post("/targets", {
        year, month, scope: "branch", scope_id: scope,
        item_targets: Object.entries(draft).filter(([, v]) => Number(v) > 0).map(([item_id, v]) => ({ item_id, target_qty: Number(v) })),
      });
      toast.success(`${branch.name} hedefleri kaydedildi`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const togglePersonnel = async (v) => {
    try {
      await http.put(`/branches/${scope}/target-settings`, { personnel_targets_enabled: v });
      toast.success(v ? "Personel hedefleri açıldı" : "Personel hedefleri kapatıldı");
      load();
    } catch { toast.error("Güncellenemedi"); }
  };
  const exportXlsx = async () => {
    const res = await http.get(`/targets/export?year=${year}&month=${month}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `hedef_${year}_${String(month).padStart(2, "0")}.xlsx`;
    a.click();
  };

  if (!prog) return <div className="text-slate-400">Yükleniyor...</div>;
  const dirty = branch && branch.items.some((it) => Number(draft[it.item_id] || 0) !== Number(it.target_qty || 0));
  const row = !canEdit ? prog.row : branch || prog.company;
  const title = !canEdit ? ({ employee: "Hedefim", branch: "Şube Hedefim" }[prog.view] || (user?.role === "mudur" ? "Şube Hedefim" : user?.role === "personel" ? "Hedefim" : "Karahan Teknoloji"))
    : branch ? branch.name : "Karahan Teknoloji";

  const table = (
    <div className="space-y-4">
      {canEdit && <ScopeChips prog={prog} scope={scope} setScope={setScope} />}
      {prog.can_view_me && (
        <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/60 p-1" data-testid="target-view-toggle">
          {[["", "Şube Hedefi"], ["me", "Benim Hedefim"]].map(([v, l]) => (
            <button key={v || "branch"} onClick={() => setView(v)} data-testid={`target-view-${v || "branch"}`}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${view === v ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      )}
      {branch && <PersonnelBar branch={branch} onToggle={togglePersonnel} />}
      <HgoTable title={title} row={row} meta={prog} editable={!!branch} draft={draft} setDraft={setDraft} />
      {branch && (
        <div className="flex justify-end">
          <Button disabled={!dirty} onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-branch-targets">
            <Save size={15} className="mr-1" /> {branch.name} Hedeflerini Kaydet
          </Button>
        </div>
      )}
      {branch && <PersonnelTargets branch={branch} employees={prog.employees.filter((e) => e.branch_id === branch.scope_id)} year={year} month={month} onSaved={load} />}
      {canEdit && !branch && <p className="text-xs text-slate-500">Karahan Teknoloji hedefi, tüm şube hedeflerinin toplamıdır. Hedef girmek için bir şube seçin.</p>}
      {canEdit && !branch && <BranchSummary branches={prog.branches} onPick={setScope} />}
    </div>
  );

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">{canEdit ? "Hedef Yönetimi" : title}</h1>
          <p className="text-slate-400 text-sm mt-1">{canEdit ? "Şube bazlı adet hedefleri" : "Adet hedefi ve gerçekleşme"} · {MONTH_NAMES[month - 1]} {year}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-32" data-testid="targets-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-28" data-testid="targets-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {user?.role !== "personel" && canAct(user, "export") && (
            <Button variant="outline" className="border-emerald-600/50 text-emerald-300" onClick={exportXlsx} data-testid="targets-export-excel">
              <FileSpreadsheet size={15} className="mr-1" /> Excel
            </Button>
          )}
        </div>
      </header>

      {canEdit ? (
        <Tabs defaultValue="table">
          <TabsList className="bg-slate-900/60 border border-slate-800">
            <TabsTrigger value="table" data-testid="tab-target-table"><Table2 size={14} className="mr-1" /> Hedef Tablosu</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-target-settings"><Settings2 size={14} className="mr-1" /> Hedef Ayarları</TabsTrigger>
          </TabsList>
          <TabsContent value="table" className="mt-4">{table}</TabsContent>
          <TabsContent value="settings" className="mt-4">
            <TargetSettings year={year} month={month} setup={prog.setup} products={products} catalogs={catalogs} onChanged={load} />
          </TabsContent>
        </Tabs>
      ) : table}
    </div>
  );
}
